import { createAsyncThunk, createSlice,  PayloadAction } from "@reduxjs/toolkit";
import onahReducer, { OnahPersistConfig } from "../../persistor";
import { BaseResponse } from "interface/baseResponse";
import { OnboardingStatus } from "interface/onboardingStatus";
import { getOnboardingStatus } from "services/onboarding";
import { Routes } from "routes/routes";
import { reduceObject } from "helper";

export type OnboardingSummary = {
    started: boolean;
    bizInfo: {
        profile: boolean;
        contact: boolean;
        address: boolean;
        infoVerify: boolean;
    };
    bizReg: {
        information: boolean;
        regVerify: boolean;
    };
    shareholder: {
        overview: boolean;
        structure: boolean;
    };
    account: boolean;
}

export type OnboardingSummaryKey = keyof OnboardingSummary;

type OnboardingState = {
    loading: boolean;
    error: boolean;
    onboarding?: OnboardingStatus;
    onboardingSummary: OnboardingSummary;
    completionPercentage?: number;
}

const initialState: OnboardingState = {
    loading: false,
    error: false,
    onboarding: undefined,
    onboardingSummary: {
        started: true,
        bizInfo: {
            profile: true,
            contact: true,
            address: true,
            infoVerify: true,
        },
        bizReg: {
            information: true,
            regVerify: true,
        },
        shareholder: {
            overview: true,
            structure: true,
        },
        account: true,
    }
}

type CheckStatusProps = {
    onSuccess?: ((summary: OnboardingSummary) => void)
    onFailed?: (() => void)
}

export const getOnboardingNextScreen = (summary: OnboardingSummary) => {
    const screens = {
        started: Routes.getStarted,
        bizInfo: Routes.businessInfo,
        bizReg: Routes.businessRegistration,
        shareholder: Routes.businessOnboardingPeople,
        account: Routes.businessOnboardingAccount,
    }
    const keys = Object.keys(screens);
    for (let key of keys) {
        const screenStatus = summary[key as keyof typeof screens]
        if(typeof  screenStatus === 'object') {
            const boolValue = Object.values(screenStatus).every(r => r);
            if(!boolValue) {
                return screens[key as keyof typeof screens]
            }
        } else {
            if(!screenStatus) {
                return screens[key as keyof typeof screens];
            }
        }
    }
    return Routes.businessOnboardingSummary;
};

export const checkOnboardingStatus = createAsyncThunk<BaseResponse<OnboardingStatus> | undefined, CheckStatusProps>('onboarding/status', async ({onFailed, onSuccess}, apiThunk) => {
    apiThunk.dispatch(setLoading(true));
    apiThunk.dispatch(setError(false));
    const response = await getOnboardingStatus();
    apiThunk.dispatch(setLoading(false));
    if(response) {
        // account, 
        const {info, registration, shareholder} = response.data!
        const summary: OnboardingSummary = {
            // account: account,
            account: true,
            bizInfo: {
                profile:  [info.name].every(r => r),
                contact:  [info.email].every(r => r),
                address:  [info.address].every(r => r),
                infoVerify:  [registration.PROOF_OF_BUSINESS_ADDRESS].every(r => r),
            },
            bizReg: {
                information: [registration.tin].every(r => r),
                regVerify: [registration.FORM_CAC].every(r => r), 
            },
            shareholder: {
                overview: [shareholder?.firstName].every(r => r),
                structure: [shareholder?.largestShare].every(r => r),
            },
            started: [info.registeredYear, info.category].every(r => r),
        }
        const values = Object.values(reduceObject(summary));
        const valuesCompleted = Object.values(values).filter(r => r);
        apiThunk.dispatch(setCompletionPercentage((valuesCompleted.length / values.length) * 100));
        apiThunk.dispatch(setSummary(summary));
        onSuccess && onSuccess(summary);
    } else {
        apiThunk.dispatch(setError(true));
        onFailed && onFailed();
    }
    return response;
    
});

export const onboardingSlice = createSlice({
    name:"onb",
    initialState: initialState,
    reducers:{
        setLoading:(state, action: PayloadAction<boolean>) => {
            state.loading = action.payload
        },
        setError:(state, action: PayloadAction<boolean>) => {
            state.error = action.payload
        },
        setSummary:(state, action: PayloadAction<OnboardingSummary>) => {
            state.onboardingSummary = action.payload
        },
        setCompletionPercentage:(state, action: PayloadAction<number>) => {
            state.completionPercentage = action.payload
        },
    },
    extraReducers:(builders)=> {
        builders.addCase(checkOnboardingStatus.fulfilled, (state, action) => {
            if(action.payload) {
                state.onboarding = action.payload.data;
            }
        });
    }
})
export const {setLoading, setError, setSummary, setCompletionPercentage} = onboardingSlice.actions;

const authPersistConfig: OnahPersistConfig = {
    key: onboardingSlice.name,
    blacklist: ['error', 'loading']
}

export const onboardingReducer = onahReducer(authPersistConfig,  onboardingSlice.reducer, true);

export const userReducerPath = onboardingSlice.name;